import { Button, Input, Modal, Table, Tag, Tooltip, Typography } from "antd";
import { CheckOutlined, CloseOutlined, WarningOutlined } from "@ant-design/icons";
import { useState } from "react";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import { approveSandwich, waiveSandwich } from "../../redux/actions/hr/salaryActions";

const { Text } = Typography;

const TYPE_COLORS = {
  saturday_sandwich: "orange",
  monday_sandwich: "volcano",
  weekly_3plus: "red",
};
const TYPE_LABELS = {
  saturday_sandwich: "Sat Sandwich",
  monday_sandwich: "Mon Sandwich",
  weekly_3plus: "3+ Absences/Week",
};

/**
 * Reusable component to display sandwich flags with approve/waive actions.
 * Props: flags (array), onDecisionMade (callback to refresh), readOnly (bool)
 */
const SandwichFlagTable = ({ flags = [], onDecisionMade, readOnly = false }) => {
  const dispatch = useDispatch();
  const [waiveModal, setWaiveModal] = useState(null); // flag id
  const [waiveReason, setWaiveReason] = useState("");
  const [actionLoading, setActionLoading] = useState(null);

  const handleApprove = async (flagId) => {
    setActionLoading(flagId);
    await dispatch(approveSandwich(flagId));
    setActionLoading(null);
    onDecisionMade?.();
  };

  const handleWaive = async () => {
    if (!waiveReason.trim()) return;
    setActionLoading(waiveModal);
    await dispatch(waiveSandwich(waiveModal, waiveReason));
    setActionLoading(null);
    setWaiveModal(null);
    setWaiveReason("");
    onDecisionMade?.();
  };

  if (!flags.length) return null;

  const columns = [
    {
      title: "Type", dataIndex: "type", width: 140,
      render: (v) => <Tag color={TYPE_COLORS[v] || "default"} icon={<WarningOutlined />}>{TYPE_LABELS[v] || v}</Tag>,
    },
    {
      title: "Week Of", dataIndex: "week_start", width: 100,
      render: (v) => dayjs(v).format("DD-MMM"),
    },
    { title: "Detail", dataIndex: "detail", ellipsis: true },
    {
      title: "Deduction", dataIndex: "extra_deduction", width: 80, align: "center",
      render: (v) => <Text strong>{v} day</Text>,
    },
    {
      title: "Status", dataIndex: "status", width: 100, align: "center",
      render: (v) => (
        <Tag color={v === "pending" ? "warning" : v === "approved" ? "error" : "success"}>
          {v.toUpperCase()}
        </Tag>
      ),
    },
    ...(!readOnly ? [{
      title: "Action", key: "action", width: 180, align: "center",
      render: (_, r) => {
        if (r.status !== "pending") {
          return (
            <Tooltip title={r.status === "waived" ? `Waived: ${r.waive_reason}` : `Approved`}>
              <Text type="secondary">{r.decision_by ? `by ${r.decision_by}` : ""}</Text>
            </Tooltip>
          );
        }
        return (
          <>
            <Button
              size="small" type="primary" danger icon={<CheckOutlined />}
              loading={actionLoading === r.id}
              onClick={() => handleApprove(r.id)}
              style={{ marginRight: 4 }}
            >
              Deduct
            </Button>
            <Button
              size="small" icon={<CloseOutlined />}
              onClick={() => { setWaiveModal(r.id); setWaiveReason(""); }}
            >
              Waive
            </Button>
          </>
        );
      },
    }] : []),
  ];

  const pendingCount = flags.filter((f) => f.status === "pending").length;

  return (
    <>
      {pendingCount > 0 && (
        <Tag color="warning" icon={<WarningOutlined />} style={{ marginBottom: 8, fontSize: 13 }}>
          {pendingCount} sandwich leave(s) pending admin review
        </Tag>
      )}
      <Table
        dataSource={flags}
        columns={columns}
        rowKey="id"
        size="small"
        pagination={false}
        bordered
        rowClassName={(r) => r.status === "pending" ? "ant-table-row-warning" : ""}
      />
      <Modal
        title="Waive Sandwich Leave"
        open={!!waiveModal}
        onOk={handleWaive}
        onCancel={() => setWaiveModal(null)}
        okText="Waive (No Deduction)"
        okButtonProps={{ loading: !!actionLoading, disabled: !waiveReason.trim() }}
      >
        <p>Provide a reason for waiving this sandwich leave deduction:</p>
        <Input.TextArea
          rows={3}
          value={waiveReason}
          onChange={(e) => setWaiveReason(e.target.value)}
          placeholder="e.g. Worker had medical emergency, pre-approved leave..."
        />
      </Modal>
    </>
  );
};

export default SandwichFlagTable;
